import { useCallback, useEffect, useRef, useState } from 'react';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { trackEvent } from '../utils/amplitude';

/**
 * Inline Paddle checkout, extracted from `MyAppSettings.tsx`.
 *
 * It lived inline there, and the creation wizard needs the same thing — two
 * copies of Paddle initialization in one repository is how they drift until one
 * of them silently stops locking the customer email.
 *
 * One difference from the agent's hook it is ported from: this one takes a LIST
 * of items. Containers buys a plan and its extras together, and they have to
 * reach Paddle as one checkout for the same reason they reach it as one
 * `subscriptions.update` on the instant path — the buyer either pays for all of
 * it or none of it.
 */

interface OpenCheckoutOptions {
  /** Plan first, then one per extra. */
  items?: Array<{ priceId: string; quantity?: number }>;
  /**
   * An existing Paddle transaction to complete instead of `items` — the one
   * Paddle builds to replace the card on a subscription.
   */
  transactionId?: string;
  /** CSS class of the container the inline frame renders into. */
  frameTarget?: string;
  /**
   * The account's Paddle customer id. LOCKS the email — the buyer cannot type a
   * different one, which is what keeps one back4app account mapped to one Paddle
   * customer. Always prefer it over `customerEmail`.
   */
  customerId?: string;
  /** Prefill, for when there is no customer id yet. */
  customerEmail?: string;
  /** Read by the webhook and by the confirm mutation's ownership check. */
  customData?: Record<string, unknown>;
  /**
   * `checkoutId` is Paddle's `che_…` — it exists only in this event, never on
   * the transaction the server can fetch, so the browser is the one that can
   * pass it on for the billing record.
   */
  onCompleted?: (transactionId?: string, checkoutId?: string) => void;
  onClosed?: () => void;
  /**
   * Tags the funnel events of this checkout. Without it nothing is tracked, so
   * a caller that does not say where the checkout came from adds no rows.
   */
  tracking?: CheckoutTracking;
  /** Inline layout. `one-page` unless the caller asks for `express`. */
  variant?: CheckoutVariant;
}

/**
 * Paddle's inline layout. `one-page`: email, country and card on one screen.
 * `express`: one-tap wallet buttons first (PayPal, Google Pay, Apple Pay, as
 * Paddle picks them), the card form behind a "Card" button.
 */
export type CheckoutVariant = 'one-page' | 'express';

/** Where the checkout was opened from, sent on every funnel event. */
export interface CheckoutTracking {
  source: string;
  /** Filled in by the hook from `variant`. */
  variant?: CheckoutVariant;
  app_id?: string;
  plan_id?: string;
}

/**
 * What happens INSIDE the Paddle iframe, same mapping as the Agent's hook.
 * Without these the funnel can't tell "never saw the form" from "saw it and
 * left" from "tried to pay and was declined". No card or email data is sent;
 * the payment itself stays `containers_payment_succeeded`, from the webhook.
 */
const FUNNEL_EVENTS: Record<string, string> = {
  'checkout.loaded': 'containers_checkout_loaded',
  'checkout.payment.initiated': 'containers_payment_initiated',
  'checkout.payment.failed': 'containers_payment_failed',
};

/**
 * Everything else Paddle reports from inside the frame goes out as one
 * `containers_checkout_event` carrying Paddle's own name: an error or a
 * warning shown to the buyer, a checkout that failed to load, the payment
 * method picked, the country changed. Our own testers can pay, so what real
 * buyers see is only knowable from here. Not forwarded: the events above,
 * which have their own names, and the frame plumbing that says nothing about
 * the buyer (`checkout.ping.size` fires on every resize).
 */
const NOT_FORWARDED = new Set([
  ...Object.keys(FUNNEL_EVENTS),
  'checkout.completed',
  'checkout.closed',
  'checkout.ping.size',
  'checkout.removespinner',
  'checkout.frame.revealed',
]);

/** Paddle's own message for an error, kept short for the analytics row. */
const errorFields = (error?: { type?: string; code?: string; detail?: string }) =>
  error
    ? { error_type: error.type, error_code: error.code, error_detail: error.detail?.slice(0, 200) }
    : {};

const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_TOKEN;
const PADDLE_ENV =
  process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox';

/**
 * `lazy`: initialize Paddle when a checkout is opened, not when the screen
 * mounts — and again on every open.
 *
 * Paddle keeps ONE event callback for the page: a second `initializePaddle`
 * becomes `Paddle.Update`, and the last caller's callback replaces everyone
 * else's. A notice that can sit on a screen next to another checkout (the
 * legacy plan change in Settings) must not take that callback just by
 * rendering, and must hold it again when it is the one being used.
 */
export default function usePaddleCheckout(options: { lazy?: boolean } = {}) {
  const { lazy = false } = options;
  const paddleRef = useRef<Paddle | null>(null);
  // Kept in a ref, not in state: the callbacks belong to the checkout currently
  // open, and Paddle's event callback is registered once at initialization.
  const callbacksRef = useRef<
    Pick<OpenCheckoutOptions, 'onCompleted' | 'onClosed'>
  >({});
  // Same reason as the callbacks: the tags belong to the checkout open now.
  // `completed` keeps the close that follows a payment out of
  // containers_checkout_closed, which means "left without paying". `seen`
  // sends each other Paddle event once per checkout, per error code.
  const trackingRef = useRef<{ tags?: CheckoutTracking; completed: boolean; seen: Set<string> }>({
    completed: false,
    seen: new Set(),
  });
  const [ready, setReady] = useState(false);

  const initialize = useCallback(
    () =>
      initializePaddle({
        token: PADDLE_TOKEN as string,
        environment: PADDLE_ENV,
        eventCallback: (event) => {
          const { tags } = trackingRef.current;
          // `che_…`, the id Paddle support asks for.
          const checkoutId = (event.data as { id?: string } | undefined)?.id;

          if (event.name === 'checkout.completed') {
            const data = event.data as { id?: string; transaction_id?: string } | undefined;
            trackingRef.current.completed = true;
            callbacksRef.current.onCompleted?.(data?.transaction_id, data?.id);
          }

          if (event.name === 'checkout.closed') {
            if (tags && !trackingRef.current.completed) {
              trackEvent('containers_checkout_closed', { ...tags, checkout_id: checkoutId });
            }

            callbacksRef.current.onClosed?.();
          }

          const funnelEvent = event.name && FUNNEL_EVENTS[event.name];

          if (tags && funnelEvent) {
            trackEvent(funnelEvent, {
              ...tags,
              checkout_id: checkoutId,
              ...errorFields(event.error),
            });
          }

          // An error can arrive with only a `type`.
          const name = (event.name as string | undefined) || event.type;
          const seenKey = `${name}|${event.error?.code || ''}`;

          if (tags && name && !NOT_FORWARDED.has(name) && !trackingRef.current.seen.has(seenKey)) {
            trackingRef.current.seen.add(seenKey);
            trackEvent('containers_checkout_event', {
              ...tags,
              name,
              checkout_id: checkoutId,
              ...errorFields(event.error),
            });
          }
        },
      }),
    []
  );

  useEffect(() => {
    if (lazy || !PADDLE_TOKEN || paddleRef.current) {
      return;
    }

    let active = true;

    initialize()
      .then((paddle) => {
        if (active && paddle) {
          paddleRef.current = paddle;
          setReady(true);
        }
      })
      .catch((e) => {
        // Not fatal to the screen: `configured` stays false and the caller can
        // fall back to saying the checkout is unavailable rather than hanging.
        console.error('Paddle initialization failed', e);
      });

    return () => {
      active = false;
    };
  }, [lazy, initialize]);

  const openCheckout = useCallback(async (options: OpenCheckoutOptions) => {
    if (lazy && PADDLE_TOKEN) {
      try {
        paddleRef.current = (await initialize()) || paddleRef.current;
      } catch (e) {
        console.error('Paddle initialization failed', e);
      }
    }

    const paddle = paddleRef.current;

    if (!paddle) {
      console.error('openCheckout called before Paddle was ready');

      return;
    }

    // Paddle appends its iframe into this element. If it is not mounted yet,
    // paddle.js throws on appendChild(undefined), and an unguarded throw here
    // takes the whole screen down through the error boundary.
    if (
      options.frameTarget &&
      !document.querySelector(`.${options.frameTarget}`)
    ) {
      console.warn(
        `openCheckout: frame target ".${options.frameTarget}" is not in the DOM`
      );

      return;
    }

    callbacksRef.current = {
      onCompleted: options.onCompleted,
      onClosed: options.onClosed,
    };
    const variant = options.variant || 'one-page';
    // The variant rides on every funnel event, so the express test can split
    // the funnel by layout.
    trackingRef.current = {
      tags: options.tracking && { ...options.tracking, variant },
      completed: false,
      seen: new Set(),
    };

    if (options.tracking) {
      trackEvent('containers_checkout_opened', trackingRef.current.tags);
    }

    // Paddle takes one or the other, never both: a transaction already carries
    // its items.
    const lineItems = options.transactionId
      ? { transactionId: options.transactionId }
      : {
          items: (options.items || []).map((item) => ({
            priceId: item.priceId,
            quantity: item.quantity ?? 1,
          })),
        };

    paddle.Checkout.open({
      ...lineItems,
      customData: options.customData,
      // The id wins when present: it locks the email. Falling back to the plain
      // email is what the old inline code did unconditionally, and is what let
      // a buyer type someone else's address.
      customer: options.customerId
        ? { id: options.customerId }
        : options.customerEmail
        ? { email: options.customerEmail }
        : undefined,
      ...(options.frameTarget
        ? {
            settings: {
              displayMode: 'inline' as const,
              // The containers dashboard has one theme and it is dark. Paddle's
              // default 'light' renders dark text that disappears against it.
              theme: 'dark' as const,
              locale: 'en',
              // Hides "Not you? Change" on the checkout. The email is the
              // account's — locked by `customer.id` above — so offering to
              // change it only offers a way to pay into the wrong customer.
              allowLogout: false,
              // One screen — email (prefilled or locked) plus country and card
              // — instead of Paddle's default wizard, whose first step asks for
              // the email BEFORE showing any payment field. `express` puts the
              // one-tap wallets first. The cast:
              // paddle-js 1.4.2 types predate 'express', the Paddle.js the
              // wrapper loads from Paddle's CDN accepts it.
              variant: variant as 'one-page',
              frameTarget: options.frameTarget,
              // A number in this version of paddle-js; the inline code in
              // MyAppSettings passes a string and only compiles because the
              // Paddle instance is typed `any` there.
              frameInitialHeight: 450,
              // Transparent, so the frame reads as part of the surrounding
              // panel rather than as a white card pasted onto it. 286px is
              // Paddle's documented minimum inline width — below it the frame
              // is clipped instead of reflowing.
              frameStyle:
                'width: 100%; min-width: 286px; background-color: transparent; border: none;',
            },
          }
        : {}),
    });
  }, [lazy, initialize]);

  /**
   * Close the open checkout from our side — the ✕ on our own modal.
   *
   * Paddle answers with `checkout.closed`, so the caller's `onClosed` still
   * runs and there is one place that decides what closing means.
   */
  const closeCheckout = useCallback(() => {
    paddleRef.current?.Checkout.close();
  }, []);

  return {
    /** False when no client token is set — the caller must not offer checkout. */
    configured: Boolean(PADDLE_TOKEN),
    ready,
    openCheckout,
    closeCheckout,
  };
}
